import { ErrorModal } from "@dexteel/mesf-core";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
	Autocomplete,
	CircularProgress,
	IconButton,
	TextField,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { Link } from "react-router-dom";
import { Operator } from "../models/Operator";
import { getOperators } from "../repositories/OperatorRepository";

type Props = {
	label: string;
	value: number | null;
	onChange?: Function;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
	required?: boolean;
};

const useOperators = (onError?: (error: Error) => void) => {
	return useQuery<Operator[], Error>({
		queryKey: ["operators"],
		queryFn: ({ signal }) => getOperators(signal),
		onError: onError,
	});
};

export const OperatorSelector = ({
	label,
	value,
	onChange = () => {},
	fieldError,
	disabled = false,
	required = false,
}: Props) => {
	const [error, setError] = useState("");

	const { data: operators, isLoading } = useOperators((error) =>
		setError(error.message),
	);

	const selectedOperator =
		operators?.find((operator) => operator.OperatorId === value) || null;

	return (
		<>
			<Autocomplete
				id="clear-on-escape"
				clearOnEscape
				options={operators || []}
				getOptionLabel={(option) => option.OperatorName}
				disabled={disabled}
				renderOption={(props, option) => (
					<li {...props} data-test-id={`operator-option-${option.OperatorId}`}>
						{option.OperatorName}
					</li>
				)}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						variant="outlined"
						fullWidth
						size="small"
						error={!!fieldError}
						helperText={fieldError?.message}
						sx={{
							backgroundColor: (theme) => theme.palette.background.paper,
						}}
						inputProps={{
							...params.inputProps,
							"data-test-id": "operator-selector",
						}}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										<>
											{params.InputProps.endAdornment}
											{value ? (
												<IconButton
													component={Link}
													to={`/configuration/operators/${value}`}
													target="_blank"
													size="small"
													title="See in operators"
												>
													<VisibilityIcon fontSize="small" />
												</IconButton>
											) : null}
										</>
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedOperator}
				onChange={(event, newValue) => {
					onChange(newValue?.OperatorId);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading operators"
			/>
		</>
	);
};
